export const COUNT_COMPLETION_STEP = Object.freeze({
  LOCATION_CHOICE: 'done-location-choice',
  REVIEW: 'review-completion',
  ABANDON_CONFIRM: 'abandon-confirm',
  COMPLETED: 'completed-next-step',
});

export const COUNT_COMPLETION_NEXT_ACTION = Object.freeze({
  NONE: 'none',
  PREPARING_ORDERING: 'preparing-ordering',
  SAVINGS_REVIEW: 'savings-review',
  CREATE_DRAFTS: 'create-drafts',
  ORDERING_ERROR: 'ordering-error',
});

export const hasCompletionCountEntry = (value) => (
  value !== null && value !== undefined && value !== ''
);

/**
 * The identity a row is accounted under. With `groups` (the store's
 * working-set groups, utils/relatedItemGroups) a related group is ONE
 * countable thing: counting any member accounts for the whole group, so a
 * session holding hidden sibling rows never reports them as unaccounted.
 */
export const completionItemIdentity = (item = {}, groups = null) => {
  const identity = item.inventory_item_id ?? item.item_snapshot?.id ?? item.id ?? null;
  if (identity == null || !groups) return identity;
  return groups.groupIdByItemId?.get?.(String(identity)) ?? identity;
};

const groupCountItems = (countItems = [], workingGroups = null) => {
  const groups = new Map();
  (countItems || []).forEach((item) => {
    const identity = completionItemIdentity(item, workingGroups);
    if (identity == null) return;
    const key = String(identity);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return groups;
};

/**
 * Completion counts are per inventory item — or per related GROUP when the
 * working-set groups are supplied — not per location row. An item is
 * accounted for when at least one of its rows has an explicit count entry.
 * A blank row is never silently interpreted as zero.
 */
export const summarizeCountCompletion = (countItems = [], opts = {}) => {
  const groups = groupCountItems(countItems, opts?.groups || null);
  const countedItems = [...groups.values()].filter((rows) => (
    rows.some((item) => hasCompletionCountEntry(item.current_count))
  )).length;
  const totalItems = groups.size;
  return {
    countedItems,
    totalItems,
    unaccountedItems: Math.max(0, totalItems - countedItems),
  };
};

/**
 * Order math still receives every location row for an accounted-for identity
 * so PAR aggregation and counted-location sums remain unchanged. Only
 * identities with no count entry anywhere are removed; otherwise a blank
 * location could accidentally be treated as zero when presenting the
 * completion need.
 */
export const countItemsEligibleForCompletionOrdering = (countItems = [], opts = {}) => {
  const eligible = [];
  groupCountItems(countItems, opts?.groups || null).forEach((rows) => {
    if (rows.some((item) => hasCompletionCountEntry(item.current_count))) {
      eligible.push(...rows);
    }
  });
  return eligible;
};

export const completionOrderingDestination = () => 'ordering.html';

const pluralItems = (n) => `${n} ${n === 1 ? 'item' : 'items'}`;

/**
 * One message describing a create_draft_from_count result that was not a
 * clean success. The server's `skipped` entries carry a per-vendor `reason`:
 *
 *   - `no_vendor_assigned` is missing ordering setup the user can fix, and its
 *     `itemCount` really counts items.
 *   - anything else (`insert_failed`, …) is a server-side FAILURE whose
 *     `error` text must be surfaced, never relabelled as a setup problem.
 *     Conflating the two is how two vendors' PO inserts failing on a deleted
 *     store read as "2 items need ordering setup" (2026-08-27, store 444).
 *
 * Returns { status: 'created'|'partial'|'none', message } — message is null
 * only for a clean 'created'.
 */
export const describeDraftCreationOutcome = ({ drafts = [], skipped = [] } = {}) => {
  const draftCount = Array.isArray(drafts) ? drafts.length : 0;
  const skips = Array.isArray(skipped) ? skipped.filter(Boolean) : [];
  if (draftCount > 0 && skips.length === 0) return { status: 'created', message: null };

  const setupSkips = skips.filter((entry) => entry.reason === 'no_vendor_assigned');
  const failureSkips = skips.filter((entry) => entry.reason !== 'no_vendor_assigned');
  const setupItemCount = setupSkips.reduce(
    (total, entry) => total + (Number(entry?.itemCount) || 1),
    0,
  );
  const failedVendors = [...new Set(
    failureSkips.map((entry) => (entry?.vendorName ? String(entry.vendorName).trim() : '')).filter(Boolean),
  )];
  const failureDetail = failureSkips
    .map((entry) => (typeof entry?.error === 'string' ? entry.error.trim() : ''))
    .find(Boolean) || '';

  const failurePhrase = failureSkips.length > 0
    ? `${failedVendors.length > 0
      ? `drafts for ${failedVendors.join(', ')} could not be created`
      : 'some drafts could not be created'}${failureDetail ? ` (${failureDetail})` : ''}`
    : '';
  const setupPhrase = setupSkips.length > 0
    ? `${pluralItems(setupItemCount)} need${setupItemCount === 1 ? 's' : ''} ordering setup`
    : '';

  if (draftCount > 0) {
    const clauses = [setupPhrase, failurePhrase].filter(Boolean).join(', and ');
    return {
      status: 'partial',
      message: `${draftCount} draft ${draftCount === 1 ? 'order was' : 'orders were'} created, but ${clauses || 'some lines were skipped'}. Review the count in Ordering before continuing.`,
    };
  }

  if (failureSkips.length > 0) {
    const tail = setupPhrase ? ` ${setupPhrase[0].toUpperCase()}${setupPhrase.slice(1)}, too.` : '';
    return {
      status: 'none',
      message: `No draft orders were created — ${failurePhrase}.${tail} Your completed count is safe; try again from Ordering.`,
    };
  }
  if (setupSkips.length > 0) {
    return {
      status: 'none',
      message: `No draft orders were created because ${setupPhrase}. Your completed count is safe.`,
    };
  }
  return {
    status: 'none',
    message: 'No draft orders were created. Your completed count is safe; try again from Ordering.',
  };
};
